import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUpRight, Calendar } from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { Section } from '@/components/Section'
import { NewsCard } from '@/components/NewsCard'
import { LocalizedLink } from '@/components/LocalizedLink'
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate'
import { usePageSEO } from '@/hooks/usePageSEO'
import type { SupportedLanguage } from '@/i18n'
import { localizedRoutes } from '@/i18n/routes'
import { getNewsSortedByDate, newsKey, type NewsArticle } from '@/data/news'
import { cn } from '@/lib/utils'

/* ============================================================
   Featured article — editorial hero block for the latest news
   ============================================================ */
function FeaturedArticle({ article }: { article: NewsArticle }) {
  const { t, i18n } = useTranslation()
  const navigate = useLocalizedNavigate()
  const currentLang = (i18n.language || 'it') as SupportedLanguage
  const newsSegment = localizedRoutes.news[currentLang]

  const title = t(newsKey(article, 'title'))
  const excerpt = t(newsKey(article, 'excerpt'))
  const alt = t(newsKey(article, 'alt'))
  const dateFormatted = new Intl.DateTimeFormat(
    currentLang === 'it' ? 'it-IT' : 'en-GB',
    { dateStyle: 'long' }
  ).format(new Date(article.date))

  const href = `${newsSegment}/${article.slug}`

  return (
    <article
      aria-labelledby="news-featured-title"
      className="group relative isolate overflow-hidden bg-white border border-ink-200"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Image column — ratio controlled explicitly */}
        <div className="relative lg:col-span-7 bg-ink-900">
          <div className="relative aspect-[16/10] lg:aspect-auto lg:h-full overflow-hidden">
            <img
              src={article.image}
              alt={alt}
              className="h-full w-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]"
            />
            {/* Dark gradient overlay — industrial hero feel */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-ink-900/70 via-ink-900/20 to-transparent"
            />
            {/* Featured ribbon */}
            <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-sm bg-brand-500 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.28em] text-white shadow-[0_6px_20px_-8px_rgba(74,155,220,0.7)]">
              <span aria-hidden="true" className="block h-1.5 w-1.5 rounded-full bg-white" />
              {t('news.featuredEyebrow')}
            </div>
            {/* Engineering corner ticks */}
            <div aria-hidden="true" className="absolute top-2 right-2 flex gap-0.5">
              <span className="block h-5 w-px bg-white/80" />
              <span className="block h-2 w-px bg-white/80" />
            </div>
            <div aria-hidden="true" className="absolute bottom-2 left-2 flex gap-0.5">
              <span className="block h-2 w-px bg-brand-400" />
              <span className="block h-5 w-px bg-brand-400" />
            </div>
          </div>
        </div>

        {/* Text column */}
        <div className="relative lg:col-span-5 flex flex-col justify-between p-7 md:p-9 lg:p-10">
          {/* Top brand rule */}
          <div
            aria-hidden="true"
            className="absolute top-0 left-0 h-[3px] w-24 bg-brand-500"
          />

          <div>
            <div className="mb-5 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">
              <Calendar className="h-3.5 w-3.5" strokeWidth={2.5} />
              <time dateTime={article.date}>{dateFormatted}</time>
            </div>

            <h2
              id="news-featured-title"
              className="fx-text-balance font-bold leading-[1.1] text-ink-900 text-2xl sm:text-3xl md:text-[2rem]"
            >
              <a
                href={`/${currentLang}/${href}`}
                onClick={(e) => {
                  e.preventDefault()
                  navigate(href)
                }}
                className="focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
              >
                <span aria-hidden="true" className="absolute inset-0" />
                {title}
              </a>
            </h2>

            <p className="mt-5 text-base md:text-[17px] leading-relaxed text-ink-600 fx-text-pretty">
              {excerpt}
            </p>

            {/* Tags */}
            {article.tagKeys && article.tagKeys.length > 0 && (
              <ul
                className="mt-6 flex flex-wrap gap-2"
                aria-label={t('newsDetail.tags')}
              >
                {article.tagKeys.map((tagKey) => (
                  <li
                    key={tagKey}
                    className="rounded-sm border border-ink-200 bg-ink-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-ink-600"
                  >
                    {t(`newsData.tags.${tagKey}`)}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-brand-600 transition-colors group-hover:text-brand-700">
            {t('buttons.readMore')}
            <ArrowUpRight
              className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={2.75}
            />
          </div>
        </div>
      </div>
    </article>
  )
}

/* ============================================================
   Main component
   ============================================================ */
export default function NewsPage() {
  const { t } = useTranslation()

  usePageSEO({
    title: t('meta.news.title'),
    description: t('meta.news.description'),
    ogTitle: t('meta.news.ogTitle'),
    ogDescription: t('meta.news.ogDescription'),
    ogImage: '/images/news/global-industrie-2023.jpg',
  })

  const sorted = useMemo(() => getNewsSortedByDate(), [])
  const [featured, ...rest] = sorted

  const countLabel =
    sorted.length === 1 ? t('news.countLabelSingular') : t('news.countLabel')

  return (
    <main id="main-content" className="bg-ink-100">
      <PageHeader
        title={t('news.title')}
        subtitle={t('news.intro')}
        eyebrow={t('home.newsEyebrow')}
      />

      {/* ============================================================
          Featured latest article
          ============================================================ */}
      {featured && (
        <Section tone="default" spacing="md">
          <header className="mb-10 md:mb-12 max-w-2xl">
            <p className="mb-3 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">
              <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
              {t('news.featuredEyebrow')}
            </p>
            <h2 className="fx-text-balance font-bold leading-tight text-ink-900 text-2xl md:text-3xl">
              {t('news.listTitle')}
            </h2>
          </header>

          <FeaturedArticle article={featured} />
        </Section>
      )}

      {/* ============================================================
          Archive grid — remaining articles
          ============================================================ */}
      {rest.length > 0 && (
        <Section tone="muted" spacing="lg">
          <header className="mb-10 md:mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="mb-3 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">
                <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
                {t('news.archiveEyebrow')}
              </p>
              <h2 className="fx-text-balance font-bold leading-tight text-ink-900 text-2xl md:text-3xl">
                {t('news.archiveTitle')}
              </h2>
            </div>

            {/* Count pill — industrial flavour */}
            <div className="inline-flex items-center gap-3 self-start rounded-sm border border-ink-200 bg-white px-4 py-2 text-xs font-semibold tracking-[0.14em] text-ink-600 uppercase">
              <span
                aria-hidden="true"
                className="block h-[3px] w-5 bg-brand-500"
              />
              <span className="font-display font-bold tabular-nums text-base text-ink-900">
                {String(sorted.length).padStart(2, '0')}
              </span>
              {countLabel}
            </div>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
            {rest.map((article) => (
              <NewsCard
                key={article.slug}
                slug={article.slug}
                image={article.image}
                imageAlt={t(newsKey(article, 'alt'))}
                title={t(newsKey(article, 'title'))}
                excerpt={t(newsKey(article, 'excerpt'))}
                date={article.date}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Empty state — defensive, should not trigger with current dataset */}
      {sorted.length === 0 && (
        <Section tone="muted" spacing="lg">
          <div className="mx-auto max-w-xl text-center">
            <p className="text-base md:text-lg text-ink-600">{t('news.empty')}</p>
            <LocalizedLink
              to="home"
              className={cn(
                'mt-6 inline-flex items-center gap-2 rounded-sm border-2 border-brand-600 bg-white px-6 py-3',
                'text-sm font-bold uppercase tracking-[0.1em] text-brand-700',
                'hover:bg-brand-600 hover:text-white transition-colors'
              )}
            >
              {t('buttons.backToHome')}
            </LocalizedLink>
          </div>
        </Section>
      )}

    </main>
  )
}
